import React, { useMemo } from 'react';
import { Text, TextLink, Headline, TextButton, Dialog } from '@oneaudi/unified-web-components';
import { getPaymentValue, resolveDisclaimer } from '../../../../utils/crs-helper';
import { useServicesContext } from '../../../../../context';
import { FinanceModalRethink } from '../financeModalRethink/FinanceModalRethink';
import { AlertModal } from '../styles/FinanceRethink.styles';
import { trackLayerClose, trackCTAClick } from '../../../../../components/ctaButtons/tracking';
import { readFinance } from '../../../../utils/sessionStorage';
import { useFinance } from '../FinanceContext';
import { useChangeRateTranslations } from './changeRate-i18N';
export const SharedChangeRateLink = ({ vehicle }) => {
    var _a;
    const trackingService = useServicesContext().getAdditionalService('audi-tracking-service');
    const { getConfiguration } = useServicesContext();
    const currencyPattern = React.useMemo(() => { var _a; return (_a = getConfiguration()) === null || _a === void 0 ? void 0 : _a.currencyPattern; }, [getConfiguration]);
    const i18n = useChangeRateTranslations();
    const { dynamicFinance, updateDynamicFinance } = (_a = useFinance()) !== null && _a !== void 0 ? _a : {};
    const errorInWebcalcRef = React.useRef(false);
    const fetchUpdatedValuesFromStore = () => {
        var _a, _b, _c, _d, _e, _f, _g;
        if (!vehicleBasic) {
            return;
        }
        const vehicleId = vehicleBasic === null || vehicleBasic === void 0 ? void 0 : vehicleBasic.id;
        const financeDataFromStore = readFinance(vehicleBasic.type);
        const financing = (_a = financeDataFromStore === null || financeDataFromStore === void 0 ? void 0 : financeDataFromStore.financing) === null || _a === void 0 ? void 0 : _a[vehicleId];
        if (financing === null || financing === void 0 ? void 0 : financing.defaultResponse) {
            const { rate, rateText, calculationDisclaimer } = dynamicFinance !== null && dynamicFinance !== void 0 ? dynamicFinance : {};
            const newDynamicFinance = dynamicFinance;
            const { campaign, productParameter, defaultResponse } = financing !== null && financing !== void 0 ? financing : {};
            const getPaymentDescription = (parameters) => {
                if ((campaign === null || campaign === void 0 ? void 0 : campaign.filterText) &&
                    (!Object.keys(productParameter).length ||
                        (productParameter && productParameter['@CampaignID']))) {
                    return campaign.filterText;
                }
                if (typeof (parameters === null || parameters === void 0 ? void 0 : parameters.Description) === 'string') {
                    return parameters.Description;
                }
                if (parameters === null || parameters === void 0 ? void 0 : parameters.Description['#text']) {
                    return parameters.Description['#text'];
                }
                return '';
            };
            if ((_b = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _b === void 0 ? void 0 : _b.Parameters) {
                if ((_d = (_c = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _c === void 0 ? void 0 : _c.Parameters) === null || _d === void 0 ? void 0 : _d.Group) {
                    const group = (_f = (_e = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _e === void 0 ? void 0 : _e.Parameters) === null || _f === void 0 ? void 0 : _f.Group;
                    const newRate = Array.isArray(group)
                        ? getPaymentValue(group[0].Parameter, currencyPattern)
                        : getPaymentValue(group.Parameter, currencyPattern);
                    if (newRate !== rate) {
                        newDynamicFinance.rate = newRate;
                    }
                }
                const newRateText = getPaymentDescription((_g = defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response) === null || _g === void 0 ? void 0 : _g.Parameters);
                if (newRateText !== rateText) {
                    newDynamicFinance.rateText = newRateText;
                }
                const newCalculationDisclaimer = resolveDisclaimer(defaultResponse === null || defaultResponse === void 0 ? void 0 : defaultResponse.Response, 'CalculationDisclaimer', false);
                if (newCalculationDisclaimer !== calculationDisclaimer) {
                    newDynamicFinance.calculationDisclaimer = newCalculationDisclaimer;
                }
            }
            updateDynamicFinance(newDynamicFinance);
        }
    };
    const [isFSDialogOpen, setIsFSDialogOpen] = React.useState(false);
    const [isFSDialogOpenFromError, setIsFSDialogOpenFromError] = React.useState(false);
    const closeFocusLayer = () => {
        fetchUpdatedValuesFromStore();
        setIsFSDialogOpen(false);
    };
    const [error, setError] = React.useState('');
    React.useEffect(() => {
        if (error) {
            setIsFSDialogOpenFromError(true);
        }
    }, [error]);
    const onError = (errorMessage) => {
        console.error(errorMessage);
        closeFocusLayer();
        if (!isFSDialogOpenFromError && !error) {
            setTimeout(() => {
                setError(errorMessage);
            }, 100);
        }
    };
    const vehicleBasic = vehicle.basic;
    const financing = vehicleBasic === null || vehicleBasic === void 0 ? void 0 : vehicleBasic.financing;
    const trackLayerLinkClick = (event, rateChangeLabel) => {
        trackCTAClick(trackingService, {
            label: event.target.id === 'change-rate-text' ? rateChangeLabel : '',
            ariaLabel: event.target.id === 'change-rate-text' ? rateChangeLabel : '',
            method: '',
            target: 'same-window',
            type: event.target.id === 'change-rate-text' ? 'financeOptions' : 'financeInfo',
            url: '',
            variant: '',
        });
    };
    const setCalculationError = (calcError) => {
        errorInWebcalcRef.current = calcError;
    };
    const financeData = useMemo(() => {
        return fetchStoredFinanceData(vehicle);
    }, [dynamicFinance, vehicle, error, isFSDialogOpenFromError]);
    if (!financeData) {
        return null;
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(React.Fragment, null, financing && financeData && (React.createElement(React.Fragment, null,
            React.createElement(TextLink, { size: "sm", id: "change-rate-text", "data-testid": "change-rate-button", onClick: (event) => {
                    setIsFSDialogOpen(true);
                    trackLayerLinkClick(event, i18n.rateChange);
                }, variant: "standalone" }, i18n.rateChange)))),
        isFSDialogOpenFromError && (React.createElement(Dialog, { "aria-label": i18n.dialogErrorAriaLabel, onCloseButtonAriaLabel: i18n.dialogErrorCloseButtonAriaLabel, hasCloseButton: true, variant: "focus-large", open: isFSDialogOpenFromError, onClose: () => {
                setIsFSDialogOpenFromError(false);
                setError('');
            } },
            React.createElement(AlertModal, null,
                React.createElement(Headline, { variant: {
                        order: '4',
                        type: 'headline',
                        weight: 'normal',
                    }, tag: "h2" }, i18n.errorHeadline),
                React.createElement(Text, { tag: "span", variant: { type: 'copy', order: '2', style: 'normal' } }, i18n.errorMessage ? i18n.errorMessage : error),
                React.createElement(TextButton, { variant: "primary", onClick: () => setIsFSDialogOpenFromError(false) }, i18n.errorCloseButton)))),
        isFSDialogOpen && (React.createElement(Dialog, { type: "custom", "aria-label": i18n.dialogSuccessAriaLabel, onCloseButtonAriaLabel: i18n.dialogCloseButtonAriaLabel, hasCloseButton: true, variant: "focus-large", open: isFSDialogOpen, onClose: () => {
                closeFocusLayer();
                trackLayerClose(trackingService);
            } },
            React.createElement(FinanceModalRethink, { fullVehicle: vehicle, onClose: closeFocusLayer, onError: onError, financeData: financeData, setCalculationError: setCalculationError })))));
};
const fetchStoredFinanceData = (vehicle) => {
    var _a, _b, _c;
    const vehicleId = (_a = vehicle === null || vehicle === void 0 ? void 0 : vehicle.basic) === null || _a === void 0 ? void 0 : _a.id;
    if (!vehicleId)
        return null;
    const vehicleFinanceData = (_c = (_b = readFinance(vehicle.basic.type)) === null || _b === void 0 ? void 0 : _b.financing) === null || _c === void 0 ? void 0 : _c[vehicleId];
    if (!(vehicleFinanceData === null || vehicleFinanceData === void 0 ? void 0 : vehicleFinanceData.defaultProductData) || !(vehicleFinanceData === null || vehicleFinanceData === void 0 ? void 0 : vehicleFinanceData.defaultResponse)) {
        return null;
    }
    return vehicleFinanceData;
};
